import { useCallback, useEffect, useMemo, useRef } from 'react';
import { logger } from 'client/utils/logger/logger';
import { flow } from 'lodash-es';
import { EdmundsAPI } from 'client/data/api/api-client';

const checkJSONStreamCompleteness = data => data.finish === 'stop';

// eslint-disable-next-line consistent-return
const jsonParser = data => {
  try {
    return JSON.parse(data);
  } catch (e) {
    logger.error(e);
  }
};

export const useServerEvents = ({
  url,
  onData,
  onConnect,
  onComplete,
  onError,
  eventNames = [],
  dataTransformers = [jsonParser],
  checkStreamCompleteness = checkJSONStreamCompleteness,
  disableReconnect = false,
}) => {
  const eventSourceRef = useRef();

  const close = useCallback(() => {
    eventSourceRef.current?.close();
  }, []);

  const handleMessage = useCallback(
    event => {
      const transformedData = flow(dataTransformers)(event.data);

      if (!transformedData) {
        return;
      }

      onData(event.type, transformedData);

      if (checkStreamCompleteness(transformedData)) {
        eventSourceRef.current?.close();
        onComplete?.();
      }
    },
    [checkStreamCompleteness, dataTransformers, onComplete, onData]
  );

  const handleOpen = useCallback(() => {
    onConnect?.();
  }, [onConnect]);

  const handleError = useCallback(
    error => {
      if (disableReconnect) {
        close();
      }

      onError?.(error?.currentTarget?.readyState);

      logger.error(error, `Unhandled Error: ${error.message}`, { server_events_error: 'true' });
    },
    [onError, close, disableReconnect]
  );

  const eventsHandlersMap = useMemo(
    () => ({
      ...eventNames.reduce((agg, eventName) => ({ ...agg, [eventName]: handleMessage }), {}),
      open: handleOpen,
      error: handleError,
    }),
    [eventNames, handleError, handleMessage, handleOpen]
  );

  useEffect(() => {
    if (url) {
      eventSourceRef.current = EdmundsAPI.eventSource(url);
      Object.entries(eventsHandlersMap).forEach(([eventName, handler]) => {
        eventSourceRef.current.addEventListener(eventName, handler);
      });
    }

    return () => {
      close();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url]);

  return close;
};
